// src/components/common/Picto.tsx
// "Pictos maison" Jogolinga (Claude Design, "Jogolinga - Emojis") qui remplacent
// les emojis : trait brun gravé, une seule touche terre cuite, grille 32 px.
// Les tracés sont dans pictoData.ts (généré depuis le design).
import React from 'react';
import { PICTOS, PictoDef, PictoName } from './pictoData';
import './Picto.css';

export { PICTOS };
export type { PictoName };

// Emoji (sans teinte de peau ni sélecteur de variante) → picto maison.
// Les nombres (1️⃣, 2️⃣0️⃣, 🔟, 💯…) sont traités à part dans pictoForEmoji.
const EMOJI_TO_PICTO: Record<string, PictoName> = {
  // ── Famille
  '👩': 'mere', '👨': 'pere', '🧒': 'enfant', '👦': 'enfant', '👧': 'enfant', '👶': 'bebe',
  '👵': 'grandMere', '👴': 'grandPere',
  '👫': 'frereSoeur', '👦👧': 'frereSoeur', '👧👦': 'frereSoeur', '🧑‍🤝‍🧑': 'frereSoeur',
  '👪': 'famille', '👨‍👩‍👧‍👦': 'famille', '👨‍👩‍👦': 'famille', '👨‍👩‍👧': 'famille', '👩👨👦👧': 'famille', '👨👩👦👧': 'famille', '👥': 'famille',
  '👰': 'epouse', '🤵': 'mari', '🧔': 'oncle', '🧑': 'cousin', '👯': 'jumeaux', '💑': 'conjoint', '💏': 'conjoint', '🤵👰': 'conjoint',
  // ── Animaux
  '🦁': 'lion', '🐘': 'elephant', '🦒': 'girafe', '🐊': 'crocodile', '🐢': 'tortue',
  '🐟': 'poisson', '🐠': 'poisson', '🐐': 'chevre',
  '🐫': 'chameau', '🐪': 'chameau', '🦓': 'zebre', '🐒': 'singe', '🐵': 'singe', '🦍': 'singe',
  '🦛': 'hippopotame', '🦌': 'antilope', '🐔': 'poule', '🐓': 'poule',
  '🐸': 'grenouille', '🦉': 'hibou',
  '🐺': 'hyene', '🦅': 'aigleVautour', '🐕': 'chien', '🐶': 'chien', '🐄': 'vache', '🐮': 'vache', '🐂': 'vache',
  '🐦': 'oiseau', '🐆': 'panthere', '🐃': 'buffle', '🐈': 'chat', '🐱': 'chat', '🐑': 'mouton', '🐏': 'mouton',
  '🐎': 'cheval', '🐍': 'serpent', '🕊': 'pigeon', '🐝': 'abeille', '🐜': 'fourmi', '🦎': 'lezardCameleon',
  '🪰': 'mouche', '🐴': 'ane', '🫏': 'ane', '🦜': 'perroquet', '🦟': 'moustique', '🦋': 'papillon',
  // ── Nourriture
  '💧': 'eau', '🍚': 'riz', '🥭': 'mangue', '🌾': 'mil', '🍵': 'the', '🍞': 'pain', '🥖': 'pain',
  // 🥥 sert à « calebasse » / « vin de palme » dans les données, jamais à la noix de coco.
  '🥥': 'calebasse', '🥜': 'arachide',
  '🍌': 'banane', '🍅': 'tomate', '🌶': 'piment', '🧅': 'oignon', '🥚': 'oeuf', '🥛': 'lait', '🍢': 'brochette',
  '🥩': 'viande', '🍖': 'viande', '🧂': 'selGouts', '🍈': 'papaye', '🥣': 'bouillieTo', '🍲': 'sauceRagout', '🍛': 'sauceRagout',
  '🌽': 'mais', '🍠': 'maniocIgname', '🍬': 'sucre', '🍎': 'fruit', '🍏': 'fruit', '🍊': 'orange', '☕': 'cafe',
  '🌰': 'tamarinNere', '🫑': 'gomboPoivron', '🍽': 'repas',
  // ── Maison & objets
  '🏠': 'maison', '🏡': 'maison', '🛖': 'maison', '🥁': 'djembe', '🪕': 'kora',
  '📖': 'livre', '📚': 'livre', '📕': 'livre', '🛶': 'pirogue', '🧺': 'panier', '🧵': 'tissu',
  // ── Nature
  '☀': 'soleil', '🌞': 'soleil', '🌙': 'lune', '🌛': 'lune', '🌜': 'lune', '🌕': 'lune',
  '🌳': 'baobab', '🌧': 'pluie', '☔': 'pluie', '⛰': 'montagne', '🏔': 'montagne', '🌊': 'fleuve',
  '⭐': 'etoile', '🌟': 'etoile', '🔥': 'feu',
  // ── Émotions
  '😊': 'joie', '😀': 'joie', '😃': 'joie', '😄': 'joie', '🙂': 'joie',
  '😢': 'tristesse', '😞': 'tristesse', '😔': 'tristesse', '😭': 'tristesse',
  '😠': 'colere', '😡': 'colere', '😮': 'surprise', '😲': 'surprise', '😯': 'surprise',
  '😍': 'amour', '🥰': 'amour', '😂': 'rire', '🤣': 'rire', '😆': 'rire',
  '😴': 'fatigue', '😩': 'fatigue', '😫': 'fatigue', '🥱': 'fatigue', '😤': 'fierte', '😌': 'fierte',
  // ── Corps
  '🦶': 'pied', '👣': 'pied', '🦴': 'osDos', '🦵': 'jambe', '👁': 'oeil', '👀': 'oeil', '👃': 'nez',
  '👄': 'bouche', '🫦': 'bouche', '🫃': 'ventre', '🫘': 'reinEstomac', '👂': 'oreille', '💪': 'bras',
  '🫀': 'coeurOrganeFoie', '🫱': 'mainPeau', '🫲': 'mainPeau', '🖐': 'mainPeau', '✋': 'mainPeau', '🤚': 'mainPeau',
  '💇': 'cheveux', '🦷': 'dent', '👅': 'langue', '💅': 'ongle', '❤': 'coeur', '♥': 'coeur',
  '🧠': 'teteCerveau', '👆': 'doigt', '☝': 'doigt', '🫁': 'poumons', '🩸': 'sang', '💀': 'crane',
  // ── Couleurs
  '⚪': 'couleurBlanc', '⬜': 'couleurBlanc', '🟢': 'couleurVert', '🟩': 'couleurVert',
  '🟡': 'couleurJaune', '🟨': 'couleurJaune', '🔴': 'couleurRouge', '🟥': 'couleurRouge',
  '🔵': 'couleurBleu', '🟦': 'couleurBleu', '🟤': 'couleurMarron', '🟫': 'couleurMarron',
  '⚫': 'couleurNoir', '⬛': 'couleurNoir', '🟠': 'couleurOrange', '🟧': 'couleurOrange',
  '🟣': 'couleurViolet', '🟪': 'couleurViolet',
  // ── Temps
  '🗓': 'moisAnnee', '📆': 'jour', '📅': 'semaine', '⏰': 'heure', '🕐': 'heure', '⌚': 'heure',
  '🌅': 'matin', '🌄': 'matin', '🌇': 'soir', '🌆': 'soir', '⬅': 'hier', '➡': 'demain', '📍': 'aujourdhui',
  // ── Salutations
  '🙏': 'merci', '👋': 'auRevoir', '🤗': 'bienvenue',
  '👍': 'caVaBien', '✅': 'oui', '✔': 'oui', '❌': 'non', '✖': 'non', '🤝': 'belleFamille',

  // ── Animaux (suite)
  '🦏': 'rhinoceros', '🦆': 'canard', '🐭': 'sourisRat', '🐀': 'sourisRat', '🐁': 'sourisRat',
  '🐰': 'lievreLapin', '🐇': 'lievreLapin', '🐷': 'porc', '🐖': 'porc', '🦂': 'scorpion', '🪱': 'verDeTerre',
  '🪲': 'scarabeeInsecte', '🐞': 'scarabeeInsecte', '🦗': 'criquet', '🐗': 'phacochere', '🦊': 'renard',
  '🐿': 'ecureuil', '🦀': 'crabe', '🐌': 'escargot', '🕷': 'araignee', '🐻': 'ours', '🐋': 'baleine', '🐳': 'baleine',
  // ── Nourriture (suite)
  '🍋': 'citron', '🥕': 'carotte', '🍍': 'ananas', '🍉': 'pasteque', '🍆': 'aubergine',
  '🥦': 'legumes', '🥕🥦': 'legumes', '🥦🥕': 'legumes', '🌿': 'gomboCannelle', '🧄': 'ail', '🥒': 'concombre',
  '🫚': 'gingembre', '🥔': 'taro', '🌱': 'nere', '🥬': 'legumesFeuilles', '🌴': 'datte', '🫐': 'figue',
  '🛢': 'huile', '🫗': 'huileDePalme', '🧃': 'jusDeFruit', '🌺🥤': 'bissap', '🍺': 'biereDolo',
  '🫖🍋': 'boissonGingembreCitron', '🍜': 'vermicelles', '🍩': 'beignet', '🧆': 'beignetsDeHaricots', '🥟': 'chaussonFrit',
  // ── Maison & objets (suite)
  '🪑': 'chaiseBanc', '🚪': 'porteChambre', '🛏': 'lit', '🪟': 'fenetre', '🔑': 'cle', '🗝': 'cle', '🪵': 'table',
  '🪔': 'lampe', '💡': 'lampe', '🪞': 'miroir', '🧹': 'balai', '🧼': 'savon', '🥄': 'cuillere', '🔪': 'couteau',
  '🥤': 'gobelet', '🍾': 'bouteille', '🏺': 'canariAEau', '👜': 'sac', '🎒': 'sac', '🖊': 'stylo', '✏': 'stylo',
  '📱': 'telephone', '🪭': 'eventail', '💰': 'argent', '💵': 'argent', '🪙': 'argent',
  // ── Corps (suite)
  '🙆': 'epaule', '🤷': 'epaule', '🤨': 'sourcils', '👤': 'silhouetteCorps', '🧍': 'silhouetteCorps',
  '🗣': 'gorge', '🧣': 'cou', '👚': 'seins', '🧴': 'peau', '🤲': 'peau', '⚡': 'systemeNerveux',
  // ── Couleurs (suite)
  '🩷': 'couleurRose', '🌸': 'couleurRose', '🩶': 'couleurGris', '🎨': 'couleurCouleur',
  // ── Temps (suite)
  '⌛': 'sablier', '⏳': 'sablier', '⬅⬅': 'avantHier', '➡➡': 'apresDemain',
  '🏜❄': 'saisonSecheFroide', '🏜🔥': 'saisonSecheChaude',
  // ── Vêtements, transport & lieux
  '👕': 'vetementTissu', '👞': 'chaussure', '👟': 'chaussure', '👡': 'sandale', '🩴': 'sandale', '🧢': 'bonnet',
  '👳‍♀': 'foulardDeTete', '👳': 'foulardDeTete', '🧕': 'foulardDeTete', '🥻': 'agbada',
  '🚗': 'voiture', '🚲': 'velo', '🛣': 'routeChemin',

  // ── Combinaisons qui reprennent un picto existant
  '🥜🥣': 'arachide', '💪🤚': 'bras', '🦵👣': 'jambe', '🦶🦶': 'pied', '🫸': 'osDos', '🔙': 'osDos',
  '💋': 'bouche', '👨👩': 'famille',
  '🐟🥣': 'sauceRagout', '🥩🥣': 'sauceRagout', '🍅🥣': 'sauceRagout', '🫘🍲': 'sauceRagout',
  '🍗🐟': 'sauceRagout', '🐔🥥🍛': 'sauceRagout',
  '🍚🌶': 'riz', '🐟🍚': 'riz', '🇸🇳🍚🐟': 'riz', '🍚🥛🍨': 'riz', '🌾🍨': 'mil', '🍲🥛🍨': 'mil',
  '🌳🥤': 'baobab', '🍃': 'baobab', '❄💧': 'eau', '♨': 'eau', '🫓': 'pain', '🍎🍍🍓': 'fruit', '🍽🌽': 'mais',
  '📊': 'moisAnnee', '1⃣📊': 'moisAnnee', '🎆': 'moisAnnee', '⏱': 'heure',
  '🕌🌅': 'matin', '🕌☀': 'soleil', '🕌🌆': 'soir',

  // ── Famille (suite) et combinaisons
  '👩‍🦳': 'tante', '👨‍👩': 'famille', '👴👵': 'grandPere', '👶👶': 'jumeaux', '👨‍💼': 'mari', '👨‍💼👰': 'conjoint',
  '👨‍🦳': 'oncle', '👨‍🦰': 'oncle', '👨‍🦳👩‍🦳': 'oncle', '👨‍🦰👩‍🦰': 'oncle',
  '🤰': 'ventre', '🍯': 'abeille', '🍷': 'biereDolo', '✌': 'auRevoir', '👎': 'non', '🙅': 'non',
  '✒': 'stylo', '🫕': 'sauceRagout', '🌑': 'couleurNoir', '🦧': 'singe',
  // ── Objets, couleurs, temps, salutations, vêtements, lieux (suite)
  '🥅': 'filet', '🧶': 'corde', '🪨': 'pierre', '🌈': 'couleurMulticolore',
  '🕛': 'midiMinuit', '♾': 'toujours', '⏮': 'avant', '🍂': 'saisonDeTransition',
  '🙇': 'pardon', '📰': 'quellesNouvelles', '🙋': 'bonjourRespectueux',
  '👖': 'pantalon', '👔': 'chemise', '🏫': 'ecole', '🚉': 'gare', '🚂': 'train', '✈': 'avion', '🏗': 'construction',
  // ── Verbes
  '❓': 'demanderQuestion', '🤔': 'penser', '👉': 'allerMontrer', '👈': 'venir', '🏃': 'courirFuir',
  '🚶': 'marcher', '💺': 'sasseoir', '🕴': 'seLeverDebout', '⬆🧍': 'seLeverDebout', '✍': 'ecrire', '🔓': 'ouvrir',
  '↩': 'repondreRetourner', '📞': 'appeler', '🔍': 'chercherRetrouver', '🔎': 'chercherRetrouver', '🔎🙂': 'chercherRetrouver',
  '🤕': 'avoirMal', '⬇': 'descendreTomber', '⬆': 'monterLever', '💼': 'travailler', '🛠': 'faireOutils',
  '🔨': 'pilerMarteau', '🚪➡': 'sortir', '🧻✂': 'dechirer', '🍳': 'cuisiner', '🚿': 'seLaver', '🎬': 'commencer',
  '🏁': 'finir', '😨': 'avoirPeur', '💃': 'danser', '🦘': 'sauter', '💊': 'soigner', '🤥': 'mentir',
  '🎁': 'donner', '🎁➡': 'donner', '⏰😮': 'seReveiller', '🧠❌': 'oublier', '👨‍🏫': 'enseigner', '🍼': 'nourrir',
  '📤': 'jeter', '✂': 'couper', '🏊': 'nager', '📦🚚': 'demenager', '💭✨': 'rever', '🤧': 'tousser',
  '💦': 'mouiller', '↔': 'ecarter', '⛏': 'creuser', '🎢': 'seBalancer',

  // ── Interface
  '▶': 'lecture', '◀': 'retour', '🔄': 'recommencer', '🔒': 'cadenas', '⚠': 'attention', 'ℹ': 'info',
  '🚫': 'interdit', '📌': 'epingle', '📋': 'copier', '📝': 'notes', '💾': 'sauvegarder', '✉': 'courrier',
  '🆕': 'nouveau', '🆓': 'gratuit', '💳': 'carteBancaire', '📂': 'dossier', '📁': 'dossier', '🗂': 'dossier',
  '🐛': 'signalerUnBug', '↪': 'partager',
  // ── Récompenses
  '🌍': 'mondeLangues', '🌎': 'mondeLangues', '🌏': 'mondeLangues', '✨': 'etincelles', '👑': 'couronne',
  '🏆': 'trophee', '🚀': 'fusee', '🎉': 'fete', '🎊': 'fete', '🥇': 'medailleDor', '🥈': 'medailleDargent',
  '🥉': 'medailleDeBronze', '💎': 'diamant', '🎯': 'cible', '🎓': 'diplome', '💫': 'etoileFilante',
  // ── Thèmes & jeux
  '🏛': 'monumentHistoire', '📜': 'parchemin', '🎵': 'musique', '🎶': 'musique', '🎼': 'musique', '🎤': 'micro',
  '⚖': 'balancePolitique', '🗺': 'carteDuMonde', '⚔': 'epeesDuel', '🎭': 'masquesCulture', '🎮': 'manetteJeu',
  '🃏': 'cartesMemoire', '🔤': 'lettres', '💬': 'bulle', '👏': 'applaudir',
  // ── Mascotte & culture
  '🌺': 'fleur', '⛵': 'voilier', '🏝': 'ile', '⚓': 'ancre', '🔺': 'pyramide', '🏙': 'villeCulture',
  '🏰': 'chateau', '🛡': 'bouclier', '🏹': 'arc', '⚒': 'outils', '🏕': 'camp', '🕯': 'bougie',
  '🖋': 'plume', '🪶': 'plume', '⚱': 'urne', '🔱': 'trident', '🗿': 'statue', '🎪': 'chapiteau', '👗': 'robe',
  '🧓': 'ancienSage', '☮': 'paix', '🏷': 'etiquette', '🎸': 'guitare', '💍': 'bague', '🤼': 'lutte',
  '🫙': 'jarre', '📿': 'chapelet', '✝': 'croix', '☪': 'croissant', '⛪': 'eglise', '🕌': 'mosqueeCulture',
  // ── Conversations
  '🚌': 'bus', '🚕': 'taxiConversation', '🏦': 'banque', '🏪': 'boutique', '🛒': 'caddie', '🛍': 'sacDeCourses',
  '🧑‍✈': 'pilote', '👩‍💼': 'employeeDeBureau', '👩‍🍳': 'cuisiniere',
};

// Même emoji, dessin différent quand il illustre un mot de vocabulaire.
const VOCAB_OVERRIDES: Record<string, PictoName> = {
  '🎮': 'jouer', '🔒': 'fermer', '💬': 'direParler', '🕌': 'mosquee', '🚕': 'taxi', '🏙': 'ville',
  '✉': 'lettre', '🛒': 'acheterVendre', '🛍': 'acheterVendre', '👩‍🍳': 'cuisiner', '🔄': 'souvent',
  '✨': 'brillerDesirer', '🥇': 'couleurDore', '🥈': 'couleurArgente', '🏪': 'marche', '👑': 'prince',
  '🌍': 'douViensTu',
};

// Retire teintes de peau, sélecteurs de variante et genre (« 🏃‍♂ » → « 🏃 »).
const normalizeEmoji = (s: string) =>
  s.trim().replace(/[\u{1F3FB}-\u{1F3FF}️︎]/gu, '').replace(/‍[♀♂]/gu, '');

// « 2⃣1⃣ » → 21, « 🔟0⃣0⃣0⃣ » → 10000, « 💯 » → 100, « 🔢 » → million ;
// null si ce n'est pas un nombre.
const numberFromEmoji = (s: string): number | null => {
  if (s === '💯') return 100;
  if (s === '🔢') return 1000000; // utilisé pour « Million » dans les données
  const digits = s.replace(/🔟/gu, '1⃣0⃣');
  return /^(?:\d⃣)+$/u.test(digits) ? Number(digits.replace(/⃣/g, '')) : null;
};

/**
 * Picto maison correspondant à un emoji, ou null s'il n'y en a pas.
 * `vocab` : l'emoji illustre un mot (certains emojis ont alors un autre dessin).
 */
export const pictoForEmoji = (emoji?: string | null, context: 'ui' | 'vocab' = 'ui'): PictoName | null => {
  if (!emoji) return null;
  const key = normalizeEmoji(emoji);
  const n = numberFromEmoji(key);
  if (n !== null) {
    const name = `n${n}`;
    return name in PICTOS ? (name as PictoName) : null;
  }
  return (context === 'vocab' ? VOCAB_OVERRIDES[key] : undefined) ?? EMOJI_TO_PICTO[key] ?? null;
};

interface PictoProps {
  name: PictoName;
  /** Taille CSS (par défaut 1.15em, suit la taille de police du parent). */
  size?: number | string;
  className?: string;
  title?: string;
}

export const Picto: React.FC<PictoProps> = ({ name, size = '1.15em', className = '', title }) => {
  const p: PictoDef = PICTOS[name];
  return (
    <svg
      className={`jl-picto ${className}`}
      width={size}
      height={size}
      viewBox="0 0 32 32"
      role="img"
      aria-label={title ?? p.n}
    >
      {p.a && (
        <path
          d={p.a}
          className="jl-picto__accent"
          fillRule="evenodd"
          style={p.f ? { fill: p.f } : undefined}
        />
      )}
      {p.b && (
        <path d={p.b} className="jl-picto__accent2" fillRule="evenodd" style={p.g ? { fill: p.g } : undefined} />
      )}
      <path d={p.s} className="jl-picto__stroke" />
    </svg>
  );
};

/** Le picto en balisage SVG, pour le HTML construit en texte (hors React). */
export const pictoSvg = (name: PictoName, size: string = '1.15em'): string => {
  const p: PictoDef = PICTOS[name];
  const accent = p.a ? `<path d="${p.a}" class="jl-picto__accent" fill-rule="evenodd"${p.f ? ` style="fill:${p.f}"` : ''}/>` : '';
  const accent2 = p.b ? `<path d="${p.b}" class="jl-picto__accent2" fill-rule="evenodd"${p.g ? ` style="fill:${p.g}"` : ''}/>` : '';
  return `<svg class="jl-picto" width="${size}" height="${size}" viewBox="0 0 32 32" role="img" aria-label="${p.n}">${accent}${accent2}<path d="${p.s}" class="jl-picto__stroke"/></svg>`;
};

/**
 * Affiche l'illustration d'un mot : picto maison si l'emoji en a un,
 * sinon l'emoji d'origine tel quel.
 */
export const Illustration: React.FC<{ value?: string | null; size?: number | string; className?: string }> = ({ value, size, className }) => {
  const name = pictoForEmoji(value, 'vocab');
  if (name) return <Picto name={name} size={size} className={className} />;
  return <>{value}</>;
};

// Une séquence emoji : pictogramme (+ teinte / variante / ZWJ), keycap ou drapeau.
// Seuls les caractères affichés en emoji comptent (présentation emoji par défaut ou
// suivis de U+FE0F) : une flèche « ↔ » dans une phrase reste du texte.
const EMOJI_RE = /(?:\p{Regional_Indicator}{2}|(?:\p{Emoji_Presentation}|\p{Extended_Pictographic}️)[\u{1F3FB}-\u{1F3FF}]?(?:‍[\p{Extended_Pictographic}♀♂]️?)*|[0-9#*]️?⃣)/gu;

/** Remplace dans un texte chaque emoji qui a un picto ; les autres restent tels quels. */
export const replaceEmojis = (text: string, size?: number | string): React.ReactNode[] => {
  const out: React.ReactNode[] = [];
  let last = 0;
  for (const m of text.matchAll(EMOJI_RE)) {
    const name = pictoForEmoji(m[0]);
    if (!name) continue;
    const i = m.index ?? 0;
    if (i > last) out.push(text.slice(last, i));
    out.push(<Picto key={i} name={name} size={size} />);
    last = i + m[0].length;
  }
  if (last < text.length) out.push(text.slice(last));
  return out;
};

/** Affiche un texte d'interface en remplaçant ses emojis par les pictos maison. */
export const EmojiText: React.FC<{ text?: string | null; size?: number | string }> = ({ text, size }) =>
  text ? <>{replaceEmojis(text, size)}</> : null;

export default Picto;
